import { GlobalApi } from '@/services/api-global.js';
import { TeamsApi } from '@/services/api-teams.js';
import { router } from '@/routes';

export const state = {
  config: {},
  user: {},
  team: {},
  teamType: null,
};

export const getters = {
  /**
   * Looks up the list of auth user's roles by the current team
   * @param {Object} state
   * @returns {Array}
   */
  userRolesForTeam: (state) => {
    let roles = [];

    if (state.user && state.user.roles) {
      // Grab the current team-specific roles
      if (state.team.id && state.user.roles[state.teamType]) {
        // if we get undefined for a team then just return an empty array
        roles = state.user.roles[state.teamType][state.team.id] || [];
      }

      // merge in any global roles to the team roles.
      if (state.user.roles.global) {
        roles = [...roles, ...state.user.roles.global];
      }
    }

    return roles;
  },

  /**
   * Get the first name of the auth user
   * @param {Object} state
   * @returns {String|Null}
   */
  firstName: (state) => {
    return state.user.name ? state.user.name.split(' ')[0] : null;
  },

  /**
   * Determines if a user is logged in.
   * @TODO: currently a simple check to see if we have a user id.
   *  Make this more robust in the future
   * @param {Object} state
   * @returns {Boolean}
   */
  isLoggedIn: (state) => {
    return Boolean(state.user.id);
  },

  /**
   * Gets the team type for use in the api path
   * @param {Object} state
   * @returns {String}
   */
  teamRoot: (state) => {
    let team;
    if (state.teamType === 'org') {
      team = 'orgs';
    } else if (state.teamType === 'dev') {
      team = 'developers';
    }

    return team;
  },

  /**
   * The current team type and id part to use in the api path
   * @param {Object} state
   * @returns {String}
   */
  teamPath: (state, getters) => {
    return getters.teamRoot ? `${getters.teamRoot}/${state.team.id}` : '';
  },

  /**
   * The auth user's team id
   * @param {Object} state
   * @returns {String|Null}
   */
  teamId: (state) => {
    return state.team.id;
  },

  /**
   * Check the user's current team sso enabled
   * @returns {Boolean|Undefined}
   */
  ssoEnabled: (state) => {
    return state.team?.sso_enabled;
  },

  /**
   * Check any of the users teams for sso enabled
   * @returns {Boolean}
   */
  ssoEnabledForAnyTeams: (state, getters, rootState, rootGetters) => {
    const hasAnyOrgSso = rootGetters['teams/userOrgTeams'].findIndex((team) => team.sso_enabled) !== -1;
    const hasAnyDevSso = rootGetters['teams/userDevTeams'].findIndex((team) => team.sso_enabled) !== -1;
    return hasAnyOrgSso || hasAnyDevSso;
  },
};

export const mutations = {
  UPDATE_CONFIG(state, config) {
    state.config = config;
  },

  UPDATE_USER(state, user) {
    state.user = user;
  },

  UPDATE_TEAM(state, team) {
    state.team = team;
  },

  UPDATE_TEAM_TYPE(state, teamType) {
    state.teamType = teamType;
  },

  UPDATE_ONBOARD_FLAG(state, onboard = false) {
    state.team = { ...state.team, onboard };
  },
};

export const actions = {
  /**
   * Takes the user info added in the php template and adds it to the store
   */
  init({ commit }) {
    commit('UPDATE_CONFIG', AppConfig.config);
    commit('UPDATE_USER', AppConfig.user);
    commit('UPDATE_TEAM', AppConfig.team);
    commit('UPDATE_TEAM_TYPE', AppConfig.teamType);
  },

  /**
   * Show the current team
   * @param {String} teamId - the team id to be shown
   * @returns {Promise.<Object>} - team
   */
  showTeam({ state, commit, getters }, teamId) {
    if (teamId === getters.teamId) {
      return Promise.resolve(state.team);
    }

    return TeamsApi.show().then((team) => {
      commit('UPDATE_TEAM', team);
      return state.team;
    });
  },

  /**
   * Make updates to the current team
   * @param {Object} payload
   * @param {Object} payload.data - updated team data
   * @returns {Promise.<Object>} - team
   */
  updateTeam({ commit, state, dispatch }, { data = {} } = {}) {
    return TeamsApi.update(data)
      .then((team) => {
        commit('UPDATE_TEAM', team);
        dispatch(
          'notifications/create',
          {
            type: 'success',
            message: 'Settings successfully updated!',
          },
          { root: true }
        );

        return state.team;
      })
      .catch((err) => {
        // rethrow server validation errors
        if (err.response && err.response.status === 422) {
          throw err;

          // 502 means the 3scale service is throwing an error
          // But we still update our DB and save the changes.
          // @TODO: fix this disconnect between error and success;
        } else if (err.response && err.response.status === 502) {
          commit('UPDATE_TEAM', { ...state.team, name: data.name });
          return state.team;

          // show error message
        } else {
          dispatch(
            'notifications/create',
            {
              type: 'error',
              message: 'We are unable to send your request at this time. Please try again.',
            },
            { root: true }
          );

          throw err;
        }
      });
  },

  /**
   * Delete a user's team
   * @param {Object} Payload
   * @param {string} Payload.id - id of the team to be deleted
   * @returns {Promise}
   */
  destroyTeam({ state, dispatch }, { id, data }) {
    // store the team name because it'll be wiped in the success callback
    const teamName = state.team.name || state.team.id;

    return TeamsApi.destroy(id, data)
      .then(() => {
        // clear out everything from vuex
        dispatch('clearTeam');
        dispatch('resetStore', null, { root: true });

        // go to the team page, since they will no longer have access to this team
        router.push({ name: 'teams.index' });

        // create notification after route change so it'll show up on the team page
        dispatch(
          'notifications/create',
          {
            type: 'success',
            message: `Team ${teamName} deleted`,
          },
          { root: true }
        );
      })
      .catch((err) => {
        // handle non-validation errors here
        if (!err.response || err.response.status !== 422) {
          dispatch(
            'notifications/create',
            {
              type: 'error',
              message: `We are unable delete team ${teamName}. Please try again.`,
            },
            { root: true }
          );
        }

        // re-throw the error so the caller can handle if needed
        throw err;
      });
  },

  setTeam({ commit, state }, { team, teamType }) {
    return new Promise((resolve) => {
      commit('UPDATE_TEAM', team);
      commit('UPDATE_TEAM_TYPE', teamType);
      resolve(state.team);
    });
  },

  clearTeam({ commit }) {
    return new Promise((resolve) => {
      commit('UPDATE_TEAM', {});
      commit('UPDATE_TEAM_TYPE', null);
      resolve();
    });
  },

  getUser({ commit, state }) {
    return GlobalApi.getCurrentUser().then((user) => {
      commit('UPDATE_USER', user);
      //pull out the teams and store them on the teams vuex store
      commit('teams/SET_USER_TEAMS', user.teams, { root: true });
      return state.user;
    });
  },

  /**
   * Update the current user's profile
   * @param {Object} context - vuex context
   * @param {Object} payload
   * @param {String} payload.data - form data
   * @returns {Promise}
   */
  updateUser({ commit }, { data }) {
    return GlobalApi.updateCurrentUser(data).then((user) => {
      commit('UPDATE_USER', user);
    });
  },

  setUser({ commit }, user) {
    commit('UPDATE_USER', user);
  },

  removeOnboardFlag({ commit }) {
    commit('UPDATE_ONBOARD_FLAG', false);
  },
};
